import { createSlice, PayloadAction } from "@reduxjs/toolkit";

export interface Prompt {
  id: number;
  heading: string;
  prompt: string;
  tables: string;
  active: boolean;
  createdBy: string;
  createdDate: string;
  modifiedBy: string | null;
  modifiedDate: string | null;
  tags: {
    tagName: string;
    color: string;
    backgroundColor: string;
  }[];
}

export interface Message {
  requestId?: string;
  sessionId?: string;
  sender: string;
  dateTime: string;
  hasError?: boolean;
  showNewSession?: boolean;
  sessionExpired?: boolean;
  isAiAnalysis?: boolean;
  kpiName?: string;
  data: {
    text: string;
    graph: {
      chart_type: string;
      data: any;
      title: string;
      description: string;
      sql_query: string;
    };
    table: {
      show_table: boolean;
      show_csv: boolean;
    };
    graphSQLData: any[];
    tableSQLData: any[];
    csv: string;
  };
}

interface ClimateAssistantState {
  query: string;
  message: Message[];
  loading: boolean;
  isExpanded: boolean;
  showMore: boolean;
  visibleCount: number;
  initialLoad: boolean;
  sessionExpired: boolean;
  resetQuery: string;
  displayPromptOverlay: boolean;
  togglePromptOverlay: boolean;
  promptList: Prompt[];
  topicName: string;
  openPromptDrawer: boolean;
}

const initialState: ClimateAssistantState = {
  query: "",
  message: [],
  loading: false,
  isExpanded: false,
  showMore: false,
  visibleCount: 3,
  initialLoad: false,
  sessionExpired: false,
  resetQuery: "",
  displayPromptOverlay: false,
  togglePromptOverlay: false,
  promptList: [],
  topicName: "",
  openPromptDrawer: false,
};

const climateAssistantSlice = createSlice({
  name: "climateAssistant",
  initialState,
  reducers: {
    setQuery: (state, action: PayloadAction<string>) => {
      state.query = action.payload;
    },
    setResetQuery: (state, action: PayloadAction<string>) => {
      state.resetQuery = action.payload;
    },
    addMessage: (state, action: PayloadAction<Message>) => {
      state.message.push(action.payload);
    },
    setMessages: (state, action: PayloadAction<Message[]>) => {
      state.message = action.payload;
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    clearMessages: (state) => {
      state.message = [];
    },
    toggleAssistantWindow: (state, action: PayloadAction<boolean>) => {
      state.isExpanded = action.payload;
    },
    setVisibleCount: (state, action: PayloadAction<number>) => {
      state.visibleCount = action.payload;
    },
    setShowMore: (state, action: PayloadAction<boolean>) => {
      state.showMore = action.payload;
    },
    setInitialLoad: (state, action: PayloadAction<boolean>) => {
      state.initialLoad = action.payload;
    },
    setSessionExpired: (state, action: PayloadAction<boolean>) => {
      state.sessionExpired = action.payload;
    },
    setDisplayPromptOverlay: (state, action: PayloadAction<boolean>) => {
      state.displayPromptOverlay = action.payload;
    },
    setTogglePromptOverlay: (state, action: PayloadAction<boolean>) => {
      state.togglePromptOverlay = action.payload;
    },
    setPromptList: (state, action: PayloadAction<Prompt[]>) => {
      state.promptList = action.payload;
    },
    setTopicName: (state, action: PayloadAction<string>) => {
      state.topicName = action.payload;
    },
    togglePromptDrawer: (state, action: PayloadAction<boolean>) => {
      state.openPromptDrawer = action.payload;
    },
    resetState: () => initialState, // full reset
  },
});

export const {
  setQuery,
  setResetQuery,
  addMessage,
  setMessages,
  setLoading,
  clearMessages,
  toggleAssistantWindow,
  setVisibleCount,
  setShowMore,
  setInitialLoad,
  setSessionExpired,
  setDisplayPromptOverlay,
  setTogglePromptOverlay,
  setPromptList,
  resetState,
  setTopicName,
  togglePromptDrawer,
} = climateAssistantSlice.actions;

export default climateAssistantSlice.reducer;
